// Stillhouse view — Overview (Dashboard). Live fleet + session summary.
import React, { useEffect, useState } from "react";
import { C, F, Pill, Dot, Btn, LatencyMeter } from "../brand/apo";
import { Card, Avatar, PageHeader, Stat, EmptyState, COPPER } from "../brand/sh";
import { useAuth } from "../auth";
import { api, type FleetMachine, type FleetSession } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

export function Overview() {
  const { user } = useAuth();
  const [machines, setMachines] = useState<FleetMachine[] | null>(null);
  const [sessions, setSessions] = useState<FleetSession[] | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .getMachines()
      .then((r) => alive && setMachines(r.machines))
      .catch(() => alive && setMachines([]));
    api
      .getSessions()
      .then((r) => alive && setSessions(r.live))
      .catch(() => alive && setSessions([]));
    return () => {
      alive = false;
    };
  }, []);

  const mlist = machines ?? [];
  const slist = sessions ?? [];
  const online = mlist.filter((m) => m.status === "ONLINE" || m.status === "IN_USE").length;
  const inUse = mlist.filter((m) => m.status === "IN_USE").length;
  const offline = mlist.filter((m) => m.status === "OFFLINE").length;
  const avgRtt = slist.length
    ? Math.round(slist.reduce((a, s) => a + (s.rttMs ?? 0), 0) / slist.length)
    : null;

  return (
    <React.Fragment>
      <PageHeader
        crumb="OVERVIEW › CONTROL PLANE"
        title={user?.org?.name ?? "—"}
        sub="One stillhouse running the whole MoonShine fleet — machines, people, policy and spend in a single pane."
      >
        <Btn ghost>Export report</Btn>
        <Btn primary>Provision machine</Btn>
      </PageHeader>
      <Scroll>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
          <Stat label="Machines online" value={`${online}/${mlist.length}`} sub={`${offline} offline · ${inUse} in use`} accent={C.glowDp} />
          <Stat label="Live sessions" value={String(slist.length)} sub={avgRtt != null ? `avg ${avgRtt} ms round-trip` : "no active sessions"} accent={C.glow}>
            {avgRtt != null && <LatencyMeter ms={avgRtt} max={32} width="100%" />}
          </Stat>
          <Stat label="In use" value={String(inUse)} sub="machines streaming now" accent={C.lilac} />
          <Stat label="Plan" value={user?.org?.plan ?? "—"} sub="organisation tier" accent={COPPER} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 16 }}>
          <Card
            title="Live sessions"
            tag={
              <Pill color={C.glow} size="sm" glow>
                {slist.length} STREAMING
              </Pill>
            }
            action={<span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint, letterSpacing: "0.12em" }}>REAL-TIME · 2s</span>}
            pad={false}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1.4fr 1fr 0.7fr 0.8fr", gap: 14, padding: "10px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
              <span>MACHINE</span>
              <span>OPERATOR</span>
              <span>DEVICE</span>
              <span>RTT</span>
              <span>BANDWIDTH</span>
            </div>
            {slist.length === 0 && <EmptyState title="No live sessions" sub="ACTIVE STREAMS APPEAR HERE" />}
            {slist.map((s) => (
              <div key={s.id} style={{ display: "grid", gridTemplateColumns: "1.3fr 1.4fr 1fr 0.7fr 0.8fr", gap: 14, padding: "13px 18px", borderBottom: `1px solid ${C.line}`, alignItems: "center", fontFamily: F.ui, fontSize: 13 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <Dot color={C.glow} size={6} />
                  <span style={{ fontFamily: F.mono, fontWeight: 700, letterSpacing: "0.03em" }}>{s.machine}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <Avatar name={s.operator} size={26} />
                  <span style={{ color: C.fgDark }}>{s.operator}</span>
                </div>
                <div style={{ color: C.fgMute }}>{s.device ?? "—"}</div>
                <div style={{ fontFamily: F.mono, color: C.glow, fontWeight: 700 }}>{s.rttMs != null ? `${s.rttMs} ms` : "—"}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontFamily: F.mono, fontSize: 12 }}>{s.bandwidthMbps != null ? `${s.bandwidthMbps} Mbps` : "—"}</span>
                  {s.rttMs != null && <LatencyMeter ms={s.rttMs} max={32} width={70} />}
                </div>
              </div>
            ))}
          </Card>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title="Needs attention" pad={false}>
              <EmptyState title="Nothing needs attention" sub="ALERTS APPEAR HERE" />
            </Card>
            <Card title="Activity" pad={false} style={{ flex: 1 }}>
              <EmptyState title="No recent activity" sub="FLEET EVENTS APPEAR HERE" />
            </Card>
          </div>
        </div>
      </Scroll>
    </React.Fragment>
  );
}
